import { IFormValues } from '@interfaces/buildPackage';
import { PassengerData, PassengerType, TravelerFormValues } from './types';
import type { PreBookingPayload } from '@v2/views/flights/components/FlightsContent/FlightDrawer/FlightFares';
import { getHotelGuestsCount } from '@utils/filterForHotel';
import type { SelectedTicketDataType } from '../../Checkout';

export const getPassengerType = (
  index: number,
  passengerData: PassengerData
): PassengerType => {
  const { adults, children = 0, infants = 0 } = passengerData;

  if (index < adults) return 'Adult';
  if (index < adults + children) return 'Child';
  return 'Infant';
};

export const getPassengerTitle = (
  index: number,
  passengerType: PassengerType
) => {
  return `Traveler ${index + 1}: ${passengerType}`;
};

export const getTotalPassengers = (passengerData: PassengerData): number => {
  const { adults, children = 0, infants = 0 } = passengerData;
  return adults + children + infants;
};

export const initializeTravelerForm = (
  isPrimaryContact: boolean
): TravelerFormValues => ({
  firstName: '',
  middleName: '',
  lastName: '',
  ...(isPrimaryContact && {
    email: '',
    phoneNumber: '',
    phoneCountryIso: 'US',
  }),
  dateOfBirth: '',
  gender: undefined,
  title: undefined,
  frequentFlyerNumber: '',
  frequentFlyerProgram: '',
});

/**
 * Calculates the effective passenger count based on package selections.
 * Priority order: Flight passengers > Hotel guests vs Ticket quantity (whichever is higher)
 */
export const getEffectivePassengerCount = (
  packageData: Pick<
    IFormValues,
    'selected' | 'tickets' | 'roomConfigurations'
  > | null,
  flightData: Pick<PreBookingPayload, 'passengers'> | null,
  ticketData?: SelectedTicketDataType
): number => {
  // If flights are selected, use flight passenger count (highest priority)
  if (packageData?.selected?.flights && flightData?.passengers) {
    return getTotalPassengers(flightData.passengers);
  }

  // If no package data, default to 1
  if (!packageData) return 1;

  // Fallback to existing hotel/ticket logic
  if (!packageData?.selected?.tickets && !packageData?.selected?.hotel)
    return 1;

  if (packageData?.selected?.tickets && packageData?.selected?.hotel) {
    const hotelGuests = getHotelGuestsCount(
      packageData.roomConfigurations ?? []
    );
    const totalTickets = ticketData?.quantity ?? packageData.tickets ?? 0;
    return hotelGuests >= totalTickets ? hotelGuests : totalTickets;
  }

  // If only tickets are selected
  if (packageData?.selected?.tickets) {
    return ticketData?.quantity ?? packageData.tickets ?? 1;
  }

  // If only hotel is selected
  if (packageData?.selected?.hotel) {
    return getHotelGuestsCount(packageData.roomConfigurations ?? []);
  }

  return 1;
};
